import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { loginWithSocial } from '@/auth';
import { SocialProvider, startSocialSignIn } from '@/auth/utils/socialSignIn';
import { socialSignInFailure } from '@/auth/utils/socialSignInFailure';
import {
  type SocialSignupHandoff,
  clearSocialSignupHandoffs,
  readSocialSignupHandoff,
} from '@/auth/utils/socialSignupHandoff';
import { useApp } from '@/hooks/useAppContext';

import { captureSelfServiceError } from '../utils/sentryScrub';

/** The provider account belongs to an OnFrontiers account it cannot sign in to. */
export interface SocialConfirmAccount {
  provider: SocialProvider;
  email?: string;
}

export interface AccountStepSocial {
  handoff: SocialSignupHandoff | undefined;
  endHandoff: () => void;
  changeAccount: () => void;
  confirmAccount: SocialConfirmAccount | undefined;
  formKey: number;
  pending: boolean;
  handleCredential: (provider: SocialProvider, idToken: string) => Promise<void>;
  showFailure: (provider: SocialProvider, err: unknown) => void;
}

/**
 * ENG-6166: "Continue with Google" or "Continue with Microsoft" on the account
 * step. A new person gets the same hand-off the login page sends (ENG-6169),
 * held here instead of in router state, so the form opens as that provider's
 * sign-up. An existing one signs in as on the login page, and this URL loads
 * again: the page then treats them as any signed-in viewer (resume, billing
 * redirect, or the route's own redirect).
 */
export function useAccountStepSocial(onFailure: (message: string) => void): AccountStepSocial {
  const { graphql } = useApp();
  const { pathname, search, state } = useLocation();
  const navigate = useNavigate();
  // A sign-up the login page started arrives in router state (ENG-6169).
  const [handoff, setHandoff] = useState(() => readSocialSignupHandoff(state));
  const handedOver = useRef(handoff !== undefined);
  const [confirmAccount, setConfirmAccount] = useState<SocialConfirmAccount>();
  // The credentials form fixes its initial values at mount; a new prefill
  // remounts it.
  const [formKey, setFormKey] = useState(0);
  // One provider answer at a time.
  const busy = useRef(false);
  const [pending, setPending] = useState(false);
  // Closing the modal abandons an answer in flight: it must not sign anyone
  // in or touch a form that is gone.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const showFailure = (provider: SocialProvider, err: unknown) => {
    const { message, expected } = socialSignInFailure(err, provider);
    if (!expected) captureSelfServiceError(err, { step: 'account', substep: 'social_sign_in' });
    onFailure(message);
  };

  const handleCredential = async (provider: SocialProvider, idToken: string) => {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    let redirecting = false;
    try {
      const start = await startSocialSignIn(graphql, provider, idToken);
      if (!mounted.current) return;
      if (start.status === 'SIGN_IN') {
        await loginWithSocial(graphql, {
          provider,
          username: start.username,
          idToken,
          next: `${pathname}${search}`,
        });
        redirecting = true;
        return;
      }
      if (start.status === 'CONFIRM_ACCOUNT') {
        // An earlier sign-up's prefill belongs to another account.
        if (handoff) {
          setHandoff(undefined);
          setFormKey((key) => key + 1);
        }
        setConfirmAccount({ provider, email: start.email });
        return;
      }
      const next: SocialSignupHandoff = {
        social: { provider, signupToken: start.signupToken, idToken },
        email: start.email,
        firstName: start.firstName,
        lastName: start.lastName,
      };
      setConfirmAccount(undefined);
      setHandoff(next);
      setFormKey((key) => key + 1);
    } catch (err) {
      if (mounted.current) showFailure(provider, err);
    } finally {
      busy.current = false;
      if (!redirecting && mounted.current) setPending(false);
    }
  };

  return {
    handoff,
    // The sign-up lapsed: back to a password, keeping what the form holds.
    endHandoff: () => setHandoff(undefined),
    // ENG-6311 "change": not this person. Forget the sign-up, empty the form.
    changeAccount: () => {
      clearSocialSignupHandoffs();
      // Drop the login page's reference from this history entry too, keeping
      // the URL and its campaign params.
      if (handedOver.current) {
        handedOver.current = false;
        navigate(`${pathname}${search}`, { replace: true, state: undefined });
      }
      setHandoff(undefined);
      setConfirmAccount(undefined);
      setFormKey((key) => key + 1);
    },
    confirmAccount,
    formKey,
    pending,
    handleCredential,
    showFailure,
  };
}
